import { TextField, Grid } from '@mui/material';
import { getControlId } from '@veeva-ctv/utils';
import { FormikProps } from 'formik';
import { SiteApplicationInput } from '@/gql/graphql';
import {
  REGULAR_FLOW_SCREEN_NAMES,
  INVITE_FLOW_SCREEN_NAMES,
} from '@/constants/eventTrackingConstants';
import { useEffect } from 'react';
import { push } from '@/utils/matomoUtils';
import { useMultiStepFormContext } from '@/context/MultiStepFormContext';

interface PersonalDetailsFormProps {
  formik: FormikProps<SiteApplicationInput>;
  isInviteFlow?: boolean;
}

const PersonalDetailsForm = ({ formik, isInviteFlow = false }: PersonalDetailsFormProps) => {
  const formContext = useMultiStepFormContext();

  useEffect(() => {

    /* When the app loads, matomo will fire a track page view event
    the moment matomo script loads, there's no way to disable that 
    so we just set the title of the first step to name that event */
    if (isInviteFlow) {
      push(['setDocumentTitle', INVITE_FLOW_SCREEN_NAMES.personalDetails]);
    } else {
      push(['setDocumentTitle', REGULAR_FLOW_SCREEN_NAMES.personalDetails]);
    }

    /* This is needed to track page view even when user navigates back to the first step using form controls,
    since in that case the page won't be refreshed but we do need to log a page view event
    */
    if (formContext && (formContext.activeStep < formContext.prevActiveStep)) {
      push(['trackPageView']);
    }
  }, []);

  return (
    <Grid container rowSpacing={1}>
      <Grid item xs={12}>
        <TextField
          sx={{ mt: 5 }}
          id={getControlId({ type: 'text', name: 'firstName' })}
          name="firstName"
          label="First name*"
          fullWidth
          value={formik.values.firstName}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          helperText={formik.touched.firstName && formik.errors.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          id={getControlId({ type: 'text', name: 'lastName' })}
          name="lastName"
          label="Last name*"
          fullWidth
          value={formik.values.lastName}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          helperText={formik.touched.lastName && formik.errors.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          id={getControlId({ type: 'text', name: 'email' })}
          name="email"
          label="Work email*"
          fullWidth
          value={formik.values.email}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          id={getControlId({ type: 'text', name: 'jobTitle' })}
          name="jobTitle"
          label="Job title*"
          fullWidth
          value={formik.values.jobTitle}
          error={formik.touched.jobTitle && Boolean(formik.errors.jobTitle)}
          helperText={formik.touched.jobTitle && formik.errors.jobTitle}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          id={getControlId({ type: 'text', name: 'phone' })}
          name="phone"
          label="Direct phone*"
          fullWidth
          value={formik.values.phone}
          error={formik.touched.phone && Boolean(formik.errors.phone)}
          helperText={formik.touched.phone && formik.errors.phone}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
        />
      </Grid>
    </Grid>
  );
};

export default PersonalDetailsForm;
